/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import ContainerFluid from "react-bootstrap/Container";
import { Link } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import UCButton from "../../buttons/index.js";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService
} from "../../../resources/validations/validations.js";
import { UNITED_HOME_LINK } from "../../../constants/appConstants";

/* Components */

/* Styles */
import styles from "./styles.js";
import classNames from "classnames";

const messages = defineMessages({
  firstNamePlaceholderMessage: {
    id: 'airlineProgram.unitedAirlines.modal.firstNamePlaceholder'
  },
  lastNamePlaceholderMessage: {
    id: 'airlineProgram.unitedAirlines.modal.lastNamePlaceholder'
  },
  mileagePlusNumberPlaceholderMessage: {
    id: 'airlineProgram.unitedAirlines.modal.mileagePlusNumberPlaceholder'
  }
});

const Content = props => {
  const { setShowModal, unitedAirlinesSupplier, joinUnitedAirlines, travelerDetails } = props;

  const theme = useTheme();
  const classes = styles({ theme });

  const formRef = useRef(null);
  const intl = useIntl();

  const { ucJoinUnitedMockedApiValidationObj } = useValidationService();
  const [testSchema, setTestSchema] = useState(null);
  const [nameError, setNameError] = useState(false);
  const [previousNameError, setPreviousNameError] = useState("");

  useEffect(() => {
    const result = createValidationPolicyYupSchema(ucJoinUnitedMockedApiValidationObj);
    setTestSchema(result);
  }, []);

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const submitForm = async (values, { setSubmitting, setErrors }) => {
    try {
      const response = await joinUnitedAirlines(values, unitedAirlinesSupplier?.providerEntityID);
      if (response.status ===  "ERROR" && response?.errors[0]?.errorCode === '1000') {
        setNameError(true)
        setPreviousNameError(values?.membershipNumber)
      } else if (response) {
        setNameError(false)
        setShowModal(false);
      } else {
        setErrors({ apiError: "Failed to join the program. Please try again." });
      }
    } catch (error) {
      setErrors({ apiError: "An unexpected error occurred. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const checkInputValue = (values) => {
    if(nameError) {
      if(values.membershipNumber !== previousNameError) {
        setNameError(false)
      }
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  };

  return (
    <div className={classes.modalContent}>
      {testSchema && (
        <Formik
          innerRef={formRef}
          initialValues={{
            firstName: travelerDetails?.firstName,
            lastName: travelerDetails?.lastName,
            membershipNumber: ""
          }}
          validationSchema={testSchema}
          onSubmit={(values, formikHelpers) => {
            submitForm(values, formikHelpers);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting
          }) => (
            <form onSubmit={handleSubmit} onChange={checkInputValue(values)}>
              <div className={classes.inputContainer}>
                <label htmlFor="firstName" className={classes.label}>
                  <FormattedMessage id="airlineProgram.unitedAirlines.modal.firstNamePlaceholder" />
                </label>
                <Field
                  placeholder={intl.formatMessage(messages.firstNamePlaceholderMessage, { firstName: values.firstName })}
                  type="text"
                  name="firstName"
                  id="firstName"
                  className={errors.firstName && touched.firstName ? "input-error" : null}
                  disabled={true}
                />
                <ErrorMessage name="firstName" component="span" className={classes.error} />
              </div>
              <div className={classes.inputContainer}>
                <label htmlFor="lastName" className={classes.label}>
                  <FormattedMessage id="airlineProgram.unitedAirlines.modal.lastNamePlaceholder" />
                </label>
                <Field
                  placeholder={intl.formatMessage(messages.lastNamePlaceholderMessage, { lastName: values.lastName })}
                  type="text"
                  name="lastName"
                  id="lastName"
                  className={errors.lastName && touched.lastName ? "input-error" : null}
                  disabled={true}
                />
                <ErrorMessage name="lastName" component="span" className={classes.error} />
              </div>
              <div className={classes.lastInputContainer}>
                <label htmlFor="membershipNumber" className={classes.label}>
                  <FormattedMessage id="airlineProgram.unitedAirlines.modal.mileagePlusNumberPlaceholder" />
                </label>
                <Field
                  placeholder={intl.formatMessage(messages.mileagePlusNumberPlaceholderMessage, { membershipNumber: values.membershipNumber })}
                  type="text"
                  name="membershipNumber"
                  id="membershipNumber"
                  className={errors.membershipNumber && touched.membershipNumber ? "input-error" : null}
                  onKeyDown={handleKeyDown}
                />
                <ErrorMessage name="membershipNumber" component="span" className={classes.error} />
              </div>
              <div>
                <FormattedMessage id="airlineProgram.unitedAirlines.modal.notAMember" />
                <Link to={UNITED_HOME_LINK} target="_blank" className={classes.joinLinkText}>
                  <FormattedMessage id="airlineProgram.unitedAirlines.modal.joinLink" />
                </Link>
              </div>
              {
                nameError &&
                  <div className={classNames(classes.error, classes.nameError)}>
                    <FormattedMessage id="airlineProgram.unitedAirlines.modal.errorMessage" />
                  </div>
              }
              <div className={classes.btnContainer}>
                <ContainerFluid>
                  <Row className={classes.btnsRow}>
                    <Col className={classes.colCancel}>
                      <UCButton
                        onClick={() => setShowModal(false)}
                        type="default"
                        className={classes.cancelBtn}
                        text={<FormattedMessage id="airlineProgram.unitedAirlines.modal.cancelButton" />}
                      />
                    </Col>
                    <Col className={classes.colRemove}>
                      <UCButton
                        className={classes.createBtn}
                        onClick={handleFormSubmit}
                        type="primary"
                        text={<FormattedMessage id="airlineProgram.unitedAirlines.modal.connectButton" />}
                        disabled={!values.firstName || !values.lastName || !values.membershipNumber}
                        spinner={{
                          name: "joinUnitedSpinner",
                          size: 20,
                        }}
                      />
                    </Col>
                  </Row>
                </ContainerFluid>
              </div>
            </form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default Content;
